// Chemin: C:\gestion-copro\src\pages\public\MentionsLegalesPage.tsx
// Page Mentions Légales - Conformité légale française
// Session 78 : Création initiale

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Globe,
  Server,
  FileText
} from 'lucide-react';

const MentionsLegalesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header simple */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              Retour à l'accueil
            </Link>
            <img 
              src="/logo-copro-protect.png" 
              alt="Copro Protect" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mentions légales</h1>
        <p className="text-gray-500 mb-8">Dernière mise à jour : 3 février 2026</p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
          
          {/* Section 1 : Éditeur */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Éditeur du site</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Le site <strong>Copro Protect</strong> est édité par :
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="font-semibold text-gray-900">DOM TECH & SERVICES</p>
                <p>SASU au capital de 500 €</p>
                <p>RCS Boulogne-sur-Mer 939 094 231</p>
                <p>SIRET : 939 094 231 00010</p>
                <p>Code APE : 6201Z (Programmation informatique)</p>
                <div className="flex items-center gap-2 pt-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>155 rue des Coccinelles — 62180 Rang-du-Fliers, France</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 : Directeur de publication */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Directeur de la publication</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p className="font-semibold text-gray-900">Dominique SERGENT</p>
              <p>Président de DOM TECH & SERVICES</p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <a 
                  href="mailto:dominique.sergent62@gmail.com" 
                  className="flex items-center gap-2 text-blue-600 hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  dominique.sergent62@gmail.com
                </a>
                <a 
                  href="tel:+33673763095" 
                  className="flex items-center gap-2 text-blue-600 hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  06 73 76 30 95
                </a>
              </div>
            </div>
          </section>

          {/* Section 3 : Hébergement */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Hébergement</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Le site et l'application sont hébergés par :</p>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="font-semibold text-gray-900">Google Cloud Platform / Firebase</p>
                <p>Google Ireland Limited</p>
                <p>Gordon House, Barrow Street</p>
                <p>Dublin 4, Irlande</p>
                <div className="flex items-center gap-2 pt-2">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <a 
                    href="https://firebase.google.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    firebase.google.com
                  </a>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-green-800 font-medium">
                  🇫🇷 Localisation des données : France (Google Cloud Paris — europe-west9)
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Toutes les données utilisateurs sont stockées exclusivement sur des serveurs 
                  situés en France, conformément aux exigences du RGPD.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 : Propriété intellectuelle */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Propriété intellectuelle</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                L'ensemble du contenu du site Copro Protect (textes, images, graphismes, logo, icônes, 
                logiciels, base de données) est la propriété exclusive de DOM TECH & SERVICES ou de 
                ses partenaires et est protégé par les lois françaises et internationales relatives 
                à la propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication, adaptation de tout ou 
                partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, 
                sauf autorisation écrite préalable de DOM TECH & SERVICES.
              </p>
              <p>
                Toute exploitation non autorisée du site ou de son contenu sera considérée comme 
                constitutive d'une contrefaçon et poursuivie conformément aux dispositions des articles 
                L.335-2 et suivants du Code de Propriété Intellectuelle.
              </p>
            </div>
          </section>

          {/* Section 5 : Liens hypertextes */}
          <section className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Liens hypertextes</h2>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Le site Copro Protect peut contenir des liens hypertextes vers d'autres sites internet. 
                DOM TECH & SERVICES n'exerce aucun contrôle sur ces sites et décline toute responsabilité 
                quant à leur contenu ou aux pratiques de confidentialité de leurs éditeurs.
              </p>
              <p>
                La création de liens hypertextes vers le site Copro Protect est soumise à l'accord 
                préalable de DOM TECH & SERVICES.
              </p>
            </div>
          </section>

          {/* Section 6 : Droit applicable */}
          <section className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Droit applicable et juridiction</h2>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Les présentes mentions légales sont régies par le droit français. En cas de litige, 
                et après échec de toute tentative de recherche d'une solution amiable, les tribunaux 
                français seront seuls compétents pour connaître de ce litige.
              </p>
              <p>
                Pour toute question relative aux présentes mentions légales, vous pouvez nous contacter 
                à l'adresse : <a href="mailto:dominique.sergent62@gmail.com" className="text-blue-600 hover:underline">dominique.sergent62@gmail.com</a>
              </p>
            </div>
          </section>

        </div>

        {/* Liens vers autres pages légales */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/cgu" 
            className="text-blue-600 hover:underline"
          >
            Conditions générales d'utilisation
          </Link>
          <span className="text-gray-300">|</span>
          <Link 
            to="/politique-confidentialite" 
            className="text-blue-600 hover:underline"
          >
            Politique de confidentialité
          </Link>
        </div>
      </main>

      {/* Footer simple */}
      <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          © 2026 DOM TECH & SERVICES — Tous droits réservés
        </div>
      </footer>
    </div>
  );
};

export default MentionsLegalesPage;
